import React, { useEffect, useState, useCallback } from "react";
import { BookOpen, ChevronLeft, Search, MapPin, Plus, Pencil, Trash2, X, ClipboardList, Send, Phone, BarChart3, Tags } from "lucide-react";
import { toast } from "sonner";
import { ccInput, ccCard, ccBtnPink } from "./care/ui";
import LiveNow from "./LiveNow";
import { GoLiveButton } from "./modules/LiveEvents";

const API = process.env.REACT_APP_BACKEND_URL;

// Fire-and-forget outbound link analytics (website visits + calls)
const trackProvider = (id, type) =>
  fetch(`${API}/api/providers/${id}/track`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ type }),
  }).catch(() => {});

const emptyForm = { state: "", name: "", type: "", description: "", phone: "", website: "" };

const ProviderForm = ({ initial, meta, busy, onSave, onClose, title }) => {
  const [form, setForm] = useState(initial || emptyForm);
  return (
    <div className="fixed inset-0 z-[100] bg-[#374151]/40 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in" data-testid="pd-form-modal" onClick={onClose}>
      <div className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-md w-full p-6 shadow-xl animate-modal-in max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-extrabold font-heading text-[#374151]">{title}</h3>
          <button onClick={onClose} data-testid="pd-form-close" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={`${ccInput} mb-3`} data-testid="pd-form-state">
          <option value="">Select state…</option>
          {meta.states.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Program / agency name" className={`${ccInput} mb-3`} data-testid="pd-form-name" />
        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={`${ccInput} mb-3`} data-testid="pd-form-type">
          <option value="">Select category…</option>
          {(meta.types || []).map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
          {form.type && !(meta.types || []).includes(form.type) && <option value={form.type}>{form.type}</option>}
        </select>
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What this program offers…" rows={3} className={`${ccInput} mb-3 resize-none`} data-testid="pd-form-description" />
        <div className="grid grid-cols-2 gap-3 mb-4">
          <input type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" className={ccInput} data-testid="pd-form-phone" />
          <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="Website (optional)" className={ccInput} data-testid="pd-form-website" />
        </div>
        <button onClick={() => onSave(form)} disabled={busy || !form.state || !form.name.trim() || !form.type.trim()} className={`${ccBtnPink} w-full disabled:opacity-40`} data-testid="pd-form-save">
          {busy ? "Saving…" : "Save Program"}
        </button>
      </div>
    </div>
  );
};

const emptyRequest = { state: "", name: "", type: "", description: "", contact_name: "", email: "", phone: "" };

const RequestForm = ({ meta, onClose }) => {
  const [form, setForm] = useState(emptyRequest);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/providers/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Submission failed");
      toast.success("Request received — our team will review your listing.");
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#374151]/40 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in" data-testid="pd-request-modal" onClick={onClose}>
      <div className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-md w-full p-6 shadow-xl animate-modal-in max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-1">
          <h3 className="text-lg font-extrabold font-heading text-[#374151]">Request to be Listed</h3>
          <button onClick={onClose} data-testid="pd-request-close" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[11px] text-[#9CA3AF] font-medium mb-4">Tell us about your program — a NestTrack admin reviews every request before it goes live.</p>
        <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={`${ccInput} mb-3`} data-testid="pd-request-state">
          <option value="">Select state…</option>
          {meta.states.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Program / agency name" className={`${ccInput} mb-3`} data-testid="pd-request-name" />
        <input type="text" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder="Program type (e.g. Home Care / Personal Assistance)" className={`${ccInput} mb-3`} data-testid="pd-request-type" />
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What you offer…" rows={2} className={`${ccInput} mb-3 resize-none`} data-testid="pd-request-description" />
        <input type="text" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Your name" className={`${ccInput} mb-3`} data-testid="pd-request-contact" />
        <div className="grid grid-cols-2 gap-3 mb-4">
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={ccInput} data-testid="pd-request-email" />
          <input type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" className={ccInput} data-testid="pd-request-phone" />
        </div>
        <button onClick={submit} disabled={busy || !form.state || !form.name.trim() || !form.type.trim() || !form.contact_name.trim() || !form.email.trim()} className={`${ccBtnPink} w-full disabled:opacity-40`} data-testid="pd-request-submit">
          {busy ? "Submitting…" : "Submit for Review"}
        </button>
      </div>
    </div>
  );
};

const RequestsModal = ({ onClose, onReviewed }) => {
  const [requests, setRequests] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    fetch(`${API}/api/master/provider-requests`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { requests: [] }))
      .then((d) => setRequests(d.requests || []))
      .catch(() => setRequests([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (rid, action) => {
    setBusyId(rid);
    try {
      const res = await fetch(`${API}/api/master/provider-requests/${rid}/${action}`, { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error("Review failed");
      toast.success(action === "approve" ? "Listing approved and published." : "Request rejected.");
      load();
      onReviewed();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#374151]/40 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in" data-testid="pd-requests-modal" onClick={onClose}>
      <div className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-lg w-full p-6 shadow-xl animate-modal-in max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-extrabold font-heading text-[#374151]">Listing Requests</h3>
          <button onClick={onClose} data-testid="pd-requests-close" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        {requests === null ? (
          <p className="text-xs text-[#9CA3AF] font-semibold py-6 text-center">Loading…</p>
        ) : requests.length === 0 ? (
          <p className="text-xs text-[#9CA3AF] font-semibold py-6 text-center" data-testid="pd-requests-empty">No pending requests — new submissions will appear here.</p>
        ) : (
          <div className="space-y-2.5">
            {requests.map((r) => (
              <div key={r.id} className="p-3.5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl" data-testid={`pd-request-row-${r.id}`}>
                <p className="text-sm font-extrabold font-heading">{r.name} <span className="text-[10px] font-bold text-[#9CA3AF]">· {r.state}</span></p>
                <span className="inline-block mt-1 px-2 py-0.5 bg-[#E2F4E1] text-[#2F6031] text-[9px] font-extrabold uppercase rounded-full">{r.type}</span>
                {r.description && <p className="text-xs text-[#6B7280] font-medium mt-1.5">{r.description}</p>}
                <p className="text-[10px] text-[#9CA3AF] font-bold mt-1.5">{r.contact_name} · {r.email}{r.phone ? ` · ${r.phone}` : ""}</p>
                <div className="flex gap-2 mt-2.5">
                  <button onClick={() => act(r.id, "approve")} disabled={busyId === r.id} data-testid={`pd-approve-${r.id}`} className="flex-1 px-3 py-2 bg-[#E2F4E1] hover:bg-[#C4E5C2] text-[#2F6031] rounded-full text-[11px] font-extrabold transition-colors disabled:opacity-50">
                    Approve & Publish
                  </button>
                  <button onClick={() => act(r.id, "reject")} disabled={busyId === r.id} data-testid={`pd-reject-${r.id}`} className="flex-1 px-3 py-2 bg-rose-50 hover:bg-[#FCE6E9] text-rose-500 rounded-full text-[11px] font-extrabold transition-colors disabled:opacity-50">
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Master-managed directory categories: add new ones, delete unused (or cascade-delete with their programs)
const CategoriesModal = ({ onClose, onChanged }) => {
  const [cats, setCats] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetch(`${API}/api/providers/categories`)
      .then((r) => (r.ok ? r.json() : { categories: [] }))
      .then((d) => setCats(d.categories || []))
      .catch(() => setCats([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/master/providers/categories`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim() }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not add category");
      toast.success(`Category "${name.trim()}" added.`);
      setName("");
      load();
      onChanged();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const del = async (c) => {
    try {
      let res = await fetch(`${API}/api/master/providers/categories/${encodeURIComponent(c.name)}`, { method: "DELETE", credentials: "include" });
      if (res.status === 409) {
        if (!window.confirm(`"${c.name}" has ${c.programs} programs. Delete the category AND all ${c.programs} programs in it?`)) return;
        res = await fetch(`${API}/api/master/providers/categories/${encodeURIComponent(c.name)}?cascade=true`, { method: "DELETE", credentials: "include" });
      }
      if (!res.ok) throw new Error("Delete failed");
      toast.success(`Category "${c.name}" deleted.`);
      load();
      onChanged();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#374151]/40 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in" data-testid="pd-categories-modal" onClick={onClose}>
      <div className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-md w-full p-6 shadow-xl animate-modal-in max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-extrabold font-heading text-[#374151]">Directory Categories</h3>
          <button onClick={onClose} data-testid="pd-categories-close" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex gap-2 mb-4">
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="New category name…" className={ccInput} data-testid="pd-category-add-input" onKeyDown={(e) => e.key === "Enter" && add()} />
          <button onClick={add} disabled={busy || !name.trim()} className={`${ccBtnPink} shrink-0 disabled:opacity-40`} data-testid="pd-category-add-btn">
            <Plus className="w-3.5 h-3.5 inline -mt-0.5" /> Add
          </button>
        </div>
        {cats === null ? (
          <p className="text-xs text-[#9CA3AF] font-semibold py-6 text-center">Loading…</p>
        ) : (
          <div className="space-y-2">
            {cats.map((c) => (
              <div key={c.name} className="flex items-center justify-between gap-2 p-3 bg-[#FFF9F2] border border-[#FCE6E9] rounded-xl" data-testid={`pd-category-row-${c.name.replace(/\s+/g, "-")}`}>
                <p className="text-xs font-bold text-[#374151]">
                  {c.name} <span className="text-[#9CA3AF] font-semibold">· {c.programs} programs</span>
                </p>
                <button onClick={() => del(c)} title="Delete category" data-testid={`pd-category-delete-${c.name.replace(/\s+/g, "-")}`} className="w-7 h-7 rounded-full bg-rose-50 text-rose-400 hover:bg-[#FCE6E9] flex items-center justify-center transition-colors shrink-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {cats.length === 0 && <p className="text-[11px] text-[#9CA3AF] font-medium text-center py-4">No categories yet.</p>}
          </div>
        )}
      </div>
    </div>
  );
};


export default function ProviderDirectory({ currentUser, onBack, onHome }) {
  const isMaster = currentUser?.role === "master" || (currentUser?.extra_roles || []).includes("master");
  const [statsOpen, setStatsOpen] = useState(false);
  const [stats, setStats] = useState(null);
  useEffect(() => {
    if (!statsOpen || !isMaster) return;
    fetch(`${API}/api/master/provider-stats`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then(setStats)
      .catch(() => {});
  }, [statsOpen, isMaster]);
  const [meta, setMeta] = useState({ states: [], types: [] });
  const [providers, setProviders] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [stateF, setStateF] = useState("");
  const [typeF, setTypeF] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [revOpen, setRevOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [flashId, setFlashId] = useState(null);

  const loadPending = useCallback(() => {
    if (!isMaster) return;
    fetch(`${API}/api/master/provider-requests`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { pending: 0 }))
      .then((d) => setPendingCount(d.pending || 0))
      .catch(() => {});
  }, [isMaster]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [catOpen, setCatOpen] = useState(false);

  const loadMeta = useCallback(() => {
    fetch(`${API}/api/providers/meta`)
      .then((r) => (r.ok ? r.json() : { states: [], types: [] }))
      .then(setMeta)
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (stateF) params.set("state", stateF);
      if (typeF) params.set("type", typeF);
      const res = await fetch(`${API}/api/providers?${params}`);
      if (res.ok) {
        const body = await res.json();
        setProviders(body.providers);
        setTotal(body.total);
      }
    } catch { /* keep previous */ }
  }, [q, stateF, typeF]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const save = async (form) => {
    setBusy(true);
    try {
      const isEdit = Boolean(editing);
      const res = await fetch(`${API}/api/master/providers${isEdit ? `/${editing.id}` : ""}`, {
        method: isEdit ? "PATCH" : "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Save failed");
      toast.success(isEdit ? "Program updated." : "Program added to the directory.");
      setAddOpen(false);
      setEditing(null);
      const savedId = isEdit ? editing.id : body.id;
      // Guarantee the saved program is visible: clear filters that would hide it, then scroll to + highlight its card
      if (q.trim()) setQ("");
      if (stateF && stateF !== form.state) setStateF("");
      if (typeF && typeF !== form.type) setTypeF("");
      setFlashId(savedId);
      setTimeout(() => {
        document.querySelector(`[data-testid="pd-card-${savedId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 600);
      setTimeout(() => setFlashId(null), 5000);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    try {
      const res = await fetch(`${API}/api/master/providers/${p.id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Delete failed");
      toast.success("Program removed.");
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const byState = (providers || []).reduce((acc, p) => {
    (acc[p.state] = acc[p.state] || []).push(p);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#FFF8E7] text-[#374151] dark:bg-[#1F2937] dark:text-slate-200" data-testid="provider-directory">
      <header className="border-b border-[#FCE6E9] bg-white/80 backdrop-blur-xl px-4 sm:px-6 py-3.5 flex items-center justify-between sticky top-0 z-40 dark:bg-[#232D3D]/90 dark:border-[#374457]">
        <div className="flex items-center gap-3">
          {onBack && (
            <button onClick={onBack} data-testid="pd-back-btn" className="flex items-center gap-1 px-3.5 py-2 bg-white border border-[#E6F2FA] rounded-full text-xs font-bold text-[#374151] hover:bg-[#E6F2FA] transition-all dark:bg-[#273244] dark:border-[#374457] dark:text-slate-200">
              <ChevronLeft className="w-3.5 h-3.5" /> Back
            </button>
          )}
          <button onClick={onHome} className="flex items-center gap-2" data-testid="pd-home-logo" title="Back to home page">
            <img src="/assets/logo.png" alt="NestTrack" className="w-8 h-8 rounded-full border-2 border-[#FCE6E9] object-cover" />
            <span className="font-black text-lg tracking-tight font-heading hidden sm:inline">
              Nest<span className="text-[#E5A0BC]">Track</span>
            </span>
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setReqOpen(true)} data-testid="pd-request-btn" className="flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] rounded-full text-xs font-bold transition-all shadow-sm">
            <Send className="w-3.5 h-3.5" /> Request to be Listed
          </button>
          {isMaster && (
            <button onClick={() => setRevOpen(true)} data-testid="pd-requests-btn" className="flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-[#FFF2D1] border border-[#F5E3B3] text-[#A08244] rounded-full text-xs font-bold transition-all shadow-sm">
              <ClipboardList className="w-3.5 h-3.5" /> Requests
              {pendingCount > 0 && <span className="px-1.5 py-0.5 bg-rose-500 text-white text-[9px] font-black rounded-full" data-testid="pd-requests-count">{pendingCount}</span>}
            </button>
          )}
          {isMaster && (
            <button onClick={() => setStatsOpen((o) => !o)} data-testid="pd-stats-btn" className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all shadow-sm ${statsOpen ? "bg-[#374151] text-white" : "bg-white hover:bg-[#E6F2FA] border border-[#B3E0F2] text-[#3D7E9E]"}`}>
              <BarChart3 className="w-3.5 h-3.5" /> Link Analytics
            </button>
          )}
          {isMaster && (
            <button onClick={() => setCatOpen(true)} data-testid="pd-categories-btn" className="flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] rounded-full text-xs font-bold transition-all shadow-sm">
              <Tags className="w-3.5 h-3.5" /> Categories
            </button>
          )}
          {isMaster && (
            <button onClick={() => setAddOpen(true)} data-testid="pd-add-btn" className="flex items-center gap-1.5 px-4 py-2 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-xs font-bold transition-all shadow-sm">
              <Plus className="w-3.5 h-3.5" /> Add Program
            </button>
          )}
        </div>
      </header>

      {/* Live Now strip — auto-scrolling public live events; click a card to join directly */}
      <LiveNow currentUser={currentUser} page="providers" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-3 flex justify-end" data-testid="directory-golive-row">
        <GoLiveButton user={currentUser} location="directory" testid="directory-go-live" />
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div>
          <h1 className="text-3xl font-black font-heading flex items-center gap-3" data-testid="pd-title">
            <span className="w-10 h-10 rounded-full bg-[#E2F4E1] flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-[#2F6031]" />
            </span>
            State Provider Directory
          </h1>
          <p className="text-sm text-[#9CA3AF] font-medium mt-1">Verified caregiver & healthcare programs across all 50 states — waiver agencies, home care networks, and caregiver support services.</p>
        </div>

        <div className={`${ccCard} p-4 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3`} data-testid="pd-filters">
          <div className="relative">
            <Search className="w-4 h-4 text-[#9CA3AF] absolute left-4 top-1/2 -translate-y-1/2" />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search programs, services, waivers…" className={`${ccInput} !pl-10`} data-testid="pd-search" />
          </div>
          <select value={stateF} onChange={(e) => setStateF(e.target.value)} className={`${ccInput} sm:!w-48`} data-testid="pd-state-filter">
            <option value="">All States</option>
            {meta.states.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select value={typeF} onChange={(e) => setTypeF(e.target.value)} className={`${ccInput} sm:!w-56`} data-testid="pd-type-filter">
            <option value="">All Program Types</option>
            {meta.types.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        <p className="text-xs font-bold text-[#9CA3AF]" data-testid="pd-count">
          {providers === null ? "Loading directory…" : `${total} programs${stateF ? ` in ${stateF}` : " nationwide"}`}
        </p>

        {providers !== null && providers.length === 0 && (
          <p className="text-xs text-[#9CA3AF] font-semibold py-10 text-center" data-testid="pd-empty">No programs match those filters — try widening your search.</p>
        )}

        {isMaster && statsOpen && (
          <div className={`${ccCard} p-5`} data-testid="pd-stats-panel">
            <h3 className="text-sm font-extrabold font-heading flex items-center gap-2 mb-1">
              <BarChart3 className="w-4 h-4 text-[#5CB3DC]" /> Link Analytics
            </h3>
            <p className="text-[11px] text-[#9CA3AF] font-medium mb-4" data-testid="pd-stats-totals">
              {stats ? `${stats.total_clicks} website visits · ${stats.total_calls} calls tracked` : "Loading…"}
            </p>
            {stats && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#9CA3AF] mb-2">Top Programs</p>
                  <div className="space-y-1.5">
                    {stats.programs.slice(0, 8).map((pr) => (
                      <div key={pr.id} className="flex items-center justify-between gap-2 p-2.5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-xl text-[11px]" data-testid={`pd-stats-program-${pr.id}`}>
                        <span className="font-bold text-[#374151] truncate">
                          {pr.name} <span className="text-[#9CA3AF] font-semibold">· {pr.state}</span>
                        </span>
                        <span className="font-extrabold text-[#3D7E9E] shrink-0">{pr.clicks} visits · {pr.calls} calls</span>
                      </div>
                    ))}
                    {stats.programs.length === 0 && <p className="text-[11px] text-[#9CA3AF] font-medium" data-testid="pd-stats-empty">No clicks tracked yet — they appear here as visitors use the directory links.</p>}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#9CA3AF] mb-2">Interest by State</p>
                  <div className="space-y-1.5">
                    {stats.states.slice(0, 8).map((s) => (
                      <div key={s.state} className="flex items-center justify-between gap-2 p-2.5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-xl text-[11px]" data-testid={`pd-stats-state-${s.state.replace(/\s/g, "-")}`}>
                        <span className="font-bold text-[#374151]">{s.state}</span>
                        <span className="font-extrabold text-[#2F6031]">{s.clicks + s.calls} taps</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {Object.entries(byState).map(([state, list]) => (
          <section key={state} data-testid={`pd-state-${state.replace(/\s/g, "-")}`}>
            <h2 className="text-base font-extrabold font-heading flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4 text-[#E5A0BC]" /> {state}
              <span className="text-[10px] font-bold text-[#9CA3AF]">{list.length}</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {list.map((p) => (
                <div key={p.id} className={`${ccCard} p-4 transition-shadow ${flashId === p.id ? "ring-2 ring-[#7CC98F] shadow-lg shadow-green-100" : ""}`} data-testid={`pd-card-${p.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100">{p.name}</p>
                    {isMaster && (
                      <div className="flex gap-1 shrink-0">
                        <button onClick={() => setEditing(p)} title="Edit program" data-testid={`pd-edit-${p.id}`} className="w-7 h-7 rounded-full bg-[#E6F2FA] text-[#3D7E9E] hover:bg-[#D6EBF7] flex items-center justify-center transition-colors">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => remove(p)} title="Delete program" data-testid={`pd-delete-${p.id}`} className="w-7 h-7 rounded-full bg-rose-50 text-rose-400 hover:bg-[#FCE6E9] flex items-center justify-center transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                  <span className="inline-block mt-1.5 px-2.5 py-0.5 bg-[#E2F4E1] text-[#2F6031] text-[9px] font-extrabold uppercase tracking-wide rounded-full">{p.type}</span>
                  {p.description && <p className="text-xs text-[#6B7280] font-medium mt-2 leading-relaxed">{p.description}</p>}
                  {(p.phone || p.website) && (
                    <div className="flex items-center gap-2 flex-wrap mt-2">
                      {p.phone && (
                        <a href={`tel:${p.phone.replace(/[^\d+]/g, "")}`} onClick={() => trackProvider(p.id, "call")} data-testid={`pd-call-${p.id}`} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#E2F4E1] border border-[#BEE3BD] text-[#2F6031] rounded-full text-[10px] font-extrabold !no-underline hover:bg-[#C9EAC7] transition-colors">
                          <Phone className="w-3 h-3" /> Call {p.phone}
                        </a>
                      )}
                      {p.website && (
                        <a href={p.website.startsWith("http") ? p.website : `https://${p.website}`} target="_blank" rel="noreferrer" onClick={() => trackProvider(p.id, "click")} data-testid={`pd-visit-${p.id}`} className="text-[10px] font-bold text-[#5CB3DC] !no-underline hover:underline">
                          {p.website}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
      </main>

      {addOpen && <ProviderForm meta={meta} busy={busy} onSave={save} onClose={() => setAddOpen(false)} title="Add Directory Program" />}
      {editing && <ProviderForm initial={editing} meta={meta} busy={busy} onSave={save} onClose={() => setEditing(null)} title="Edit Directory Program" />}
      {reqOpen && <RequestForm meta={meta} onClose={() => setReqOpen(false)} />}
      {revOpen && <RequestsModal onClose={() => setRevOpen(false)} onReviewed={() => { load(); loadPending(); }} />}
      {catOpen && <CategoriesModal onClose={() => setCatOpen(false)} onChanged={() => { loadMeta(); load(); }} />}
    </div>
  );
}
